export interface GenerateIndividualSummaryParams {
  docketNumber: string;
  filename: string;
  filingType?: string;
  submissionId?: string;
  submissionIds?: string[];
  detailLevel?: 'basic' | 'standard' | 'detailed' | 'comprehensive';
  forceRefresh?: boolean;
}

export interface IndividualSummaryResponse {
  success: boolean;
  summary?: string;
  error?: string;
}

/**
 * Generate an individual summary for a specific file
 */
export async function generateIndividualSummary(
  params: GenerateIndividualSummaryParams
): Promise<IndividualSummaryResponse> {
  const {
    docketNumber,
    filename,
    filingType,
    submissionId,
    submissionIds,
    detailLevel = 'standard',
    forceRefresh = false
  } = params;

  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prepare-individual-summary`;
    const normalizedSubmissionIds = Array.isArray(submissionIds)
      ? submissionIds.filter((id) => typeof id === 'string' && id.trim().length > 0).map((id) => id.trim())
      : submissionId
      ? [submissionId.trim()]
      : [];
    
    const requestBody: any = {
      docketNumber,
      filename,
      detail_level: detailLevel,
    };

    // Add optional parameters
    if (filingType) {
      requestBody.filing_type = filingType;
    }
    if (forceRefresh) {
      requestBody.force_refresh = forceRefresh;
    }
    if (normalizedSubmissionIds.length > 0) {
      requestBody.submission_ids = normalizedSubmissionIds;
    }

    if (normalizedSubmissionIds.length > 0 && !filingType) {
      return {
        success: false,
        error: 'filing_type is required when submission_id is provided'
      };
    }

    console.log('📤 Generating individual summary:', requestBody);

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      console.error('❌ Individual summary generation failed:', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }

    // The response is plain text (the summary)
    const summaryText = await response.text();
    console.log('✅ Individual summary generated successfully');

    return {
      success: true,
      summary: summaryText
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to generate individual summary';
    console.error('❌ Error generating individual summary:', err);
    return {
      success: false,
      error: errorMessage
    };
  }
}

